'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { fetchAllCategories } from '@/api/category';
import { fetchAllTypes } from '@/api/type';
import { IconName } from '../icon/Icon';
import { Chips } from '../chips/Chips';

interface FiltersProps {
  className?: string;
  selectedCategories: string[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<string[]>>;
  selectedTypes: string[];
  setSelectedTypes: React.Dispatch<React.SetStateAction<string[]>>;
  border?: boolean;
}

const getLocaleFromPath = (pathname: string) => {
  const pathSegments = pathname.split('/');
  const supportedLocales = ['en', 'de'];
  return supportedLocales.includes(pathSegments[1]) ? pathSegments[1] : 'en';
};

const Filters = ({
  className,
  selectedCategories,
  setSelectedCategories,
  selectedTypes,
  setSelectedTypes,
  border,
}: FiltersProps) => {
  const [categories, setCategories] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const pathname = usePathname();
  const locale = getLocaleFromPath(pathname);
  const containerRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);
  const [showChips, setShowChips] = useState(false);

  useEffect(() => {
    const loadTypesAndCategories = async () => {
      const [fetchedTypes, fetchedCategories] = await Promise.all([
        fetchAllTypes(),
        fetchAllCategories(locale),
      ]);
      setTypes(fetchedTypes);
      setCategories(fetchedCategories);

      setTimeout(() => {
        setShowChips(true);
      }, 20);
    };

    loadTypesAndCategories();
  }, [locale]);

  const typeIconMap: Record<string, IconName> = {
    artikel: 'pen-tool',
    page: 'file',
    'flash-talk': 'zap',
    podcast: 'podcast',
    news: 'newspaper',
    'edu-talk': 'graduation-cap',
    'info-seite': 'file',
  };

  const scrollChipIntoView = (index: number) => {
    const container = containerRef.current;
    const chipsContainer = chipsRef.current;
    if (!container || !chipsContainer) return;

    const chip = chipsContainer.children[index] as HTMLElement;
    if (!chip) return;

    const containerWidth = container.offsetWidth;
    const chipLeft = chip.offsetLeft;
    const chipWidth = chip.offsetWidth;

    const scrollPosition = chipLeft - (containerWidth - chipWidth) / 2;

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });
  };

  const toggleCategory = (title: string) => {
    setSelectedCategories((currentCategories) =>
      currentCategories.includes(title)
        ? currentCategories.filter((category) => category !== title)
        : [...currentCategories, title],
    );
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedCategories([]);
  };

  return (
    <div className={className}>
      <div
        className="filters-container h-[70px] overflow-x-auto scroll-smooth py-4 md:overflow-visible"
        ref={containerRef}
      >
        <div className="inline-flex h-8 min-w-max gap-3" ref={chipsRef}>
          {showChips && (
            <>
              {/* "All" chip */}
              <Chips
                key="all"
                label="All"
                size="medium"
                variant="default"
                selected={
                  selectedTypes.length === 0 && selectedCategories.length === 0
                }
                onClick={() => {
                  clearFilters();
                  if (window.innerWidth < 768) {
                    scrollChipIntoView(0); // index 0 is "All"
                  }
                }}
                selectionMode="single"
                border={border}
                classNameLabel="capitalize text-[16px]"
                className={`!px-3 !py-1 !font-normal !text-black ${
                  selectedTypes.length === 0 && selectedCategories.length === 0
                    ? '!border-brand-400 !bg-brand-100'
                    : ''
                }`}
              />
              <Chips
                key="page"
                label="Info-Seite"
                size="medium"
                variant="default"
                selected={selectedTypes[0] === 'page'} // ✅ fix this
                onClick={() => {
                  setSelectedTypes(['page']); // ✅ not []
                  if (window.innerWidth < 768) {
                    scrollChipIntoView(1); // Adjust index accordingly
                  }
                }}
                selectionMode="single"
                border={border}
                icon={{ name: 'file', size: 16 }}
                classNameLabel="capitalize text-[16px]"
                className={`!px-3 !py-1 !font-normal !text-black ${selectedTypes[0] === 'page' ? '!border-brand-400 !bg-brand-100' : ''}`}
              />

              {/* Dynamic chips */}
              {types.map((type: any, index) => {
                const title = type.title;
                const isSelected = selectedTypes[0] === title;

                return (
                  <Chips
                    key={type.id}
                    label={title}
                    size="medium"
                    variant="default"
                    selected={isSelected}
                    onClick={() => {
                      setSelectedTypes([title]);
                      if (window.innerWidth < 768) {
                        scrollChipIntoView(index + 1); // +1 because "All" chip is at index 0
                      }
                    }}
                    selectionMode="single"
                    border={border}
                    icon={
                      typeIconMap[title]
                        ? { name: typeIconMap[title], size: 16 }
                        : { name: 'dot', size: 16 }
                    }
                    classNameLabel="capitalize text-[16px]"
                    className={`!px-3 !py-1 !font-normal !text-black ${isSelected ? '!border-brand-400 !bg-brand-100' : ''}`} // ✅ override selected color
                  />
                );
              })}
              {categories.map((category: any, index) => {
                const title = category.title;
                const isSelected = selectedCategories.includes(title);

                return (
                  <Chips
                    key={category.id}
                    label={title}
                    size="medium"
                    variant="default"
                    selected={isSelected}
                    onClick={() => {
                      toggleCategory(title);
                      if (window.innerWidth < 768) {
                        scrollChipIntoView(types.length + index + 2);
                      }
                    }}
                    selectionMode="multiple"
                    border={border}
                    classNameLabel="capitalize text-[16px]"
                    className={`!px-3 !py-1 !font-normal !text-black ${isSelected ? '!border-brand-400 !bg-brand-100' : ''}`}
                  />
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Filters;
